import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { UiButtonComponent, UiModels, UiPreloaderComponent, UiSearchComponent } from '@ui';
import { AppState } from '../../../../../../store/app.state';
import { PersonGroupsActions } from '@states/person-groups/person-groups.actions';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { PersonGroupsModel } from '@models/person-groups.model';
import { Actions, ofType } from '@ngrx/effects';
import { filter, take } from 'rxjs';
import { PersonGroupsDialogService } from '../../../../../../services/person-groups-dialog.service';

@Component({
  selector: 'app-add-person-to-groups-dialog',
  standalone: true,
  imports: [
    MatCheckbox,
    ReactiveFormsModule,
    TranslatePipe,
    UiButtonComponent,
    UiPreloaderComponent,
    UiSearchComponent,
  ],
  templateUrl: './add-person-to-groups-dialog.component.html',
  styleUrl: './add-person-to-groups-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddPersonToGroupsDialogComponent implements OnInit {
  private readonly store$ = inject(Store<AppState>);
  private readonly actions$ = inject(Actions);
  private readonly personGroupsDialogService = inject(PersonGroupsDialogService);
  private readonly data = inject<PersonGroupsModel.AddPersonToGroupsDialogData>(MAT_DIALOG_DATA);

  protected readonly searchControl = new FormControl<string>('', { nonNullable: true });
  private readonly searchQuery = signal('');
  private readonly selectedGroupIds = signal<Set<string>>(new Set<string>());

  private readonly groups = this.store$.selectSignal(personGroupsFeature.selectAllPersonGroups);
  private readonly personGroupsState = this.store$.selectSignal(personGroupsFeature.selectPersonGroups);
  protected readonly preloaderColor = UiModels.PreloaderColor;
  protected readonly isPersonGroupsLoading = computed(() => this.personGroupsState().isLoading);
  protected readonly isAddingPersonsToGroups = this.store$.selectSignal(personGroupsFeature.selectIsAddingPersonsToGroups);
  protected readonly saveDisabled = computed(
    () => this.selectedGroupIds().size === 0 || this.isAddingPersonsToGroups(),
  );

  protected readonly filteredGroups = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const groups = this.groups();

    if (!query) {
      return groups;
    }

    return groups.filter((group) => group.name.toLowerCase().includes(query));
  });

  public constructor(
    private readonly dialogRef: MatDialogRef<AddPersonToGroupsDialogComponent>,
  ) {
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((query) => this.searchQuery.set(query));
  }

  public ngOnInit(): void {
    this.store$.dispatch(PersonGroupsActions.startLoadingPersonGroups());
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected save(): void {
    if (this.saveDisabled()) {
      return;
    }

    const groupIds = [...this.selectedGroupIds()];

    this.actions$
      .pipe(
        ofType(PersonGroupsActions.addPersonsToGroupsSuccess),
        filter((action) => this.hasSameGroupIds(action.groupIds, groupIds)),
        take(1),
      )
      .subscribe(() => {
        this.dialogRef.close(true);
      });


    this.store$.dispatch(
      PersonGroupsActions.startAddingPersonsToGroups({
        groupIds,
        unsavedPersonDtos: this.data.unsavedPersonDtos ?? [],
        savedPersonIds: this.getSelectedSavedPersonIds(),
      }),
    );
  }

  protected isGroupSelected(groupId: string): boolean {
    return this.selectedGroupIds().has(groupId);
  }

  protected setGroupSelected(groupId: string, selected: boolean): void {
    this.selectedGroupIds.update((selectedGroupIds) => {
      const nextSelectedGroupIds = new Set(selectedGroupIds);

      if (selected) {
        nextSelectedGroupIds.add(groupId);
      } else {
        nextSelectedGroupIds.delete(groupId);
      }

      return nextSelectedGroupIds;
    });
  }

  protected toggleGroup(groupId: string): void {
    this.setGroupSelected(groupId, !this.isGroupSelected(groupId));
  }

  protected openCreateGroupDialog(): void {
    this.personGroupsDialogService.openCreate()
      .pipe(take(1))
      .subscribe((group) => {
        if (group) {
          this.setGroupSelected(group._id, true);
        }
      });
  }

  private getSelectedSavedPersonIds(): number[] {
    return this.data.personType === PersonGroupsModel.AddPersonToGroupDialogTab.SavedPersons
      ? this.data.personIds
      : [];
  }

  private hasSameGroupIds(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const sortedLeft = [...left].sort();
    const sortedRight = [...right].sort();

    return sortedLeft.every((id, index) => id === sortedRight[index]);
  }
}
